import axiosInstance from '@/app/api/axiosInstance';

export interface DocumentAttachment {
  id: number;
  organization_id: number;
  site_id?: number;
  entity_type: string;
  entity_id: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  storage_key: string;
  upload_status: string;
  file_metadata?: Record<string, any>;
  is_deleted: boolean;
  created_at: string;
  updated_at?: string;
  created_by: number;
  updated_by?: number;
  download_url?: string;
  view_url?: string;
}

export interface AttachmentListResponse {
  items: DocumentAttachment[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface AttachmentUploadResponse {
  attachment_id: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  upload_status: string;
  message: string;
}

export interface PresignedUrlResponse {
  url: string;
  expires_in: number;
  attachment_id: number;
  file_name: string;
}

export interface AttachmentFilter {
  entity_type?: string;
  entity_id?: number;
  organization_id?: number;
  site_id?: number;
  is_deleted?: boolean;
  mime_type?: string;
  created_by?: number;
}

export interface AttachmentUpdateData {
  file_metadata?: Record<string, any>;
  is_deleted?: boolean;
}

class AttachmentService {
  private baseUrl = '/attachments';

  async uploadAttachment(
    file: File,
    entityType: string,
    entityId: number,
    siteId?: number,
    fileMetadata?: Record<string, any>
  ): Promise<AttachmentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId.toString());
    
    if (siteId) {
      formData.append('site_id', siteId.toString());
    }
    
    if (fileMetadata) {
      formData.append('file_metadata', JSON.stringify(fileMetadata));
    }

    const { data } = await axiosInstance.post<AttachmentUploadResponse>(
      `${this.baseUrl}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  }

  async listAttachments(
    filter?: AttachmentFilter,
    page = 1,
    size = 20
  ): Promise<AttachmentListResponse> {
    const params = {
      ...filter,
      page,
      size,
    };
    const { data } = await axiosInstance.get<AttachmentListResponse>(
      this.baseUrl,
      { params }
    );
    return data;
  }

  async getEntityAttachments(
    entityType: string,
    entityId: number
  ): Promise<DocumentAttachment[]> {
    const { data } = await axiosInstance.get<DocumentAttachment[]>(
      `${this.baseUrl}/entity/${entityType}/${entityId}`
    );
    return data;
  }

  async getAttachment(attachmentId: number): Promise<DocumentAttachment> {
    const { data } = await axiosInstance.get<DocumentAttachment>(
      `${this.baseUrl}/${attachmentId}`
    );
    return data;
  }

  async getDownloadUrl(
    attachmentId: number,
    expiresIn?: number
  ): Promise<PresignedUrlResponse> {
    const params = expiresIn ? { expires_in: expiresIn } : undefined;
    const { data } = await axiosInstance.get<PresignedUrlResponse>(
      `${this.baseUrl}/${attachmentId}/download-url`,
      { params }
    );
    return data;
  }

  async getViewUrl(
    attachmentId: number,
    expiresIn?: number
  ): Promise<PresignedUrlResponse> {
    const params = expiresIn ? { expires_in: expiresIn } : undefined;
    const { data } = await axiosInstance.get<PresignedUrlResponse>(
      `${this.baseUrl}/${attachmentId}/view-url`,
      { params }
    );
    return data;
  }

  async updateAttachment(
    attachmentId: number,
    data: AttachmentUpdateData
  ): Promise<DocumentAttachment> {
    const { data: response } = await axiosInstance.put<DocumentAttachment>(
      `${this.baseUrl}/${attachmentId}`,
      data
    );
    return response;
  }

  async deleteAttachment(
    attachmentId: number,
    hardDelete = false
  ): Promise<{ message: string }> {
    console.log('🗑️ Deleting attachment:', { attachmentId, hardDelete });
    
    // Build URL with query parameter
    const url = hardDelete 
      ? `${this.baseUrl}/${attachmentId}?hard_delete=true`
      : `${this.baseUrl}/${attachmentId}`;
    
    console.log('🔗 Delete URL:', url);
    
    const { data } = await axiosInstance.delete<{ message: string }>(url);
    return data;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📑';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z')) return '🗜️';
    return '📎';
  }
}

export const attachmentService = new AttachmentService();